import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import KeyboardDoubleArrowDown from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUp from '@mui/icons-material/KeyboardDoubleArrowUp';
import { Link } from 'react-router-dom';
import { Alert, Button } from '@/ui';
import DeleteModal from '../../../../components/DeleteModal/DeleteModal';
import SaveAsEntityModal from '../../../../components/SaveAsEntityModal/SaveAsEntityModal';
import SaveEntityModal from '../../../../components/SaveEntityModal/SaveEntityModal';
import { formatDate } from '../../../../utils/conversionFunctions';
import { useParams } from 'react-router-dom';
import { SiteField } from '../sequenceUtils';
import './SequenceNav.css';
import CommandK from '../../../../components/CommandK/CommandK/CommandK';
import AuditModalButton from '../../../../components/AuditModal/AuditModal';
import DetailHeader from '../../../../components/DetailHeader/DetailHeader';

const ENTITY_TYPE = 'Sequence';
const MODAL_TYPE = ' Sequence';

const BackToZuulLink = React.memo(function BackToZuulLink({ onClick }) {
  return (
    <Button component={Link} to='/campaigns/sequences' onClick={onClick} color='link' startIcon={<ChevronLeft />}>
      Back to Zuul
    </Button>
  );
});

const ToggleButton = React.memo(function ToggleButton({ isOpen, onToggle, side }) {
  return (
    <IconButton size='small' className='toggleBtn' onClick={() => onToggle(side)} aria-label={`Toggle the ${side === 'leftAside' ? 'offer list' : 'properties panel'}`}>
      {isOpen ? <KeyboardDoubleArrowDown fontSize='small' /> : <KeyboardDoubleArrowUp fontSize='small' />}
    </IconButton>
  );
});

function SequenceNav({
  handleSubmit,
  onSubmitForm,
  submitting,
  sequenceName,
  isActive,
  toggle,
  leftAside,
  rightAside,
  toggleAside,
  getErrors,
  setConfirmedExitModalTrue,
  publishedSequence,
  sites,
}) {
  const { sequenceId } = useParams();
  const isEditMode = !!sequenceId;

  const errors = useMemo(() => getErrors(), [getErrors]);
  const shouldErrorRender = useMemo(() => Object.keys(errors).some((x) => errors[x]), [errors]);

  return (
    // The same header every other detail page has — title row with the actions, then the
    // Active toggle first on the row below. The shell keeps it pinned to the top of this
    // full-screen editor; the header itself is squared off and flush so it reads as the toolbar.
    <div className='sequence-nav-shell'>
      <DetailHeader
        isActive={Boolean(isActive)}
        title={sequenceName || 'New Sequence'}
        subtitle={
          isEditMode ? (
            <>
              <em>Sequence ID: </em>
              {sequenceId}
            </>
          ) : undefined
        }
        leading={
          <>
            <BackToZuulLink onClick={setConfirmedExitModalTrue} />
            <ToggleButton isOpen={leftAside} onToggle={toggleAside} side='leftAside' />
          </>
        }
        actions={
          <>
            <AuditModalButton />
            <SaveAsEntityModal
              getErrors={getErrors}
              onClickSubmit={handleSubmit(onSubmitForm)}
              isEditMode={isEditMode}
              disabled={isEditMode}
              entityType={ENTITY_TYPE}
              submitting={submitting}
            >
              <SiteField fieldName='saveAsSite' sites={sites} />
            </SaveAsEntityModal>
            <SaveEntityModal
              getErrors={getErrors}
              onClickSubmit={handleSubmit(onSubmitForm)}
              isEditMode={isEditMode}
              disabled={isEditMode}
              entityName={sequenceName}
              entityType={ENTITY_TYPE}
              submitting={submitting}
            />
            {isEditMode && <DeleteModal outlined modalType={MODAL_TYPE} id={sequenceId} entity={'sequences'} />}
            <ToggleButton isOpen={rightAside} onToggle={toggleAside} side='rightAside' />
            <CommandK hide />
          </>
        }
        toggle={toggle}
        sx={{ mb: 0, borderRadius: 0, borderTop: 0, borderLeft: 0, borderRight: 0, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)' }}
      >
        <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
          {shouldErrorRender && errors?._error ? (
            <Alert color='danger' sx={{ py: 0 }}>
              {errors._error}
            </Alert>
          ) : publishedSequence?.updatedAt && publishedSequence?.zuulUser?.realname ? (
            <Typography noWrap variant='body2'>
              {`Last Published at ${formatDate(publishedSequence.updatedAt)} by: ${publishedSequence.zuulUser.realname}`}
            </Typography>
          ) : null}
        </Box>
      </DetailHeader>
    </div>
  );
}

export default React.memo(SequenceNav);
